/**
 * Welche öffentlichen Seiten auf dem Server entstehen — EINE Liste.
 *
 * `next.config.ts` baut daraus die Umschreibung auf die Server-Route,
 * `app/(seiten)/…/page.tsx` wählt daraus Komponente und SEO-Schlüssel, und
 * `backend/tests/test_server_seiten_canon.py` hält die Liste gegen die Sitemap
 * (`backend/app/services/sitemap.py`, `SEITEN`). Was hier fehlt, bleibt eine
 * Seite der Single-Page-App — Crawler ohne JavaScript sehen dort nur die Hülle.
 *
 * Reines Modul ohne Imports: `next.config.ts` lädt es beim Start.
 */

export type ServerSeite = 'start' | 'backtest' | 'monte_carlo' | 'entnahmeplan' | 'berater' | 'watchlist' | 'depot_check' | 'fire' | 'av_depot' | 'vorabpauschale' | 'preise' | 'ueber_uns' | 'entwickler' | 'community' | 'architekten' | 'profil' | 'lexikon' | 'lexikon_kennzahl' | 'studie_entnahme' | 'musterportfolios' | 'musterportfolio' | 'av_ratgeber' | 'backtest_tools' | 'pv_alternative' | 'methodik' | 'ratgeber_4_prozent';

/** Pfad hinter dem Sprach-Präfix → Seite. `''` ist die Startseite. */
export const SERVER_SEITEN: Record<string, ServerSeite> = {
  '': 'start',
  '/portfolio-backtester': 'backtest',
  '/monte-carlo-simulation': 'monte_carlo',
  '/entnahmeplan': 'entnahmeplan',
  '/fuer-berater': 'berater',
  '/watchlist': 'watchlist',
  '/depot-check': 'depot_check',
  '/fire': 'fire',
  '/altersvorsorgedepot-rechner': 'av_depot',
  '/vorabpauschale-rechner': 'vorabpauschale',
  '/pricing': 'preise',
  '/about': 'ueber_uns',
  '/developers': 'entwickler',
  '/community-backtests': 'community',
  '/community-backtests/architects': 'architekten',
  // Kennzahlen-Lexikon (seit 25.10.2026): die Übersicht und je Kennzahl eine Seite —
  // dieselben Adressen wie `components/lexikon/lexikon.ts` (`test_lexikon_canon.py`).
  '/learn': 'lexikon',
  '/learn/cagr': 'lexikon_kennzahl',
  '/learn/volatility': 'lexikon_kennzahl',
  '/learn/max-drawdown': 'lexikon_kennzahl',
  '/learn/drawdown-duration': 'lexikon_kennzahl',
  '/learn/ulcer-index': 'lexikon_kennzahl',
  '/learn/sharpe-ratio': 'lexikon_kennzahl',
  '/learn/sortino-ratio': 'lexikon_kennzahl',
  '/learn/calmar-ratio': 'lexikon_kennzahl',
  '/learn/sterling-ratio': 'lexikon_kennzahl',
  '/learn/martin-ratio': 'lexikon_kennzahl',
  '/learn/recovery-ratio': 'lexikon_kennzahl',
  // Datenstudie „Sichere Entnahmerate“ (seit 25.10.2026, `components/studien/entnahmeStudie.ts`).
  '/research/safe-withdrawal-rate': 'studie_entnahme',
  // Musterportfolios (seit 25.10.2026): die Übersicht und je Portfolio eine Seite —
  // dieselben Adressen wie `components/musterportfolios/musterportfolios.ts` (`test_musterportfolios.py`).
  '/model-portfolios': 'musterportfolios',
  '/model-portfolios/world-equity': 'musterportfolio',
  '/model-portfolios/world-portfolio-70-30': 'musterportfolio',
  '/model-portfolios/80-20': 'musterportfolio',
  '/model-portfolios/60-40': 'musterportfolio',
  '/model-portfolios/40-60': 'musterportfolio',
  '/model-portfolios/permanent-portfolio': 'musterportfolio',
  '/model-portfolios/all-weather': 'musterportfolio',
  '/model-portfolios/golden-butterfly': 'musterportfolio',
  // Ratgeber Altersvorsorgedepot (seit 25.10.2026, nur `/de/deutsch`, siehe `NUR_IN`) —
  // dieselben Adressen wie `components/avRatgeber/avRatgeber.ts`.
  '/altersvorsorgedepot': 'av_ratgeber',
  '/altersvorsorgedepot/zulagen': 'av_ratgeber',
  '/altersvorsorgedepot/vergleich': 'av_ratgeber',
  '/altersvorsorgedepot/erlaubte-fonds': 'av_ratgeber',
  '/altersvorsorgedepot/auszahlung': 'av_ratgeber',
  '/altersvorsorgedepot/erbfall': 'av_ratgeber',
  // Vergleich der Backtest-Werkzeuge und Methodik (seit 25.10.2026, elf Sprachen).
  '/backtest-tools': 'backtest_tools',
  '/portfolio-visualizer-alternative': 'pv_alternative',
  '/methodology': 'methodik',
  // Ratgeber unter derselben Adresse (seit 25.10.2026) — `RATGEBER` in `components/lexikon/lexikon.ts`.
  '/learn/4-percent-rule': 'ratgeber_4_prozent',
};

/**
 * Seiten, die es nur in EINER Sprachfassung gibt — Pfad → Land und Sprach-Präfix
 * (`code` ist die Sprache der Fassung). Das Produkt dahinter gilt nur in einem
 * Land: Das Altersvorsorgedepot ist deutsches Recht (Entscheidung 18.10.2026),
 * die Vorabpauschale ebenso (§ 18 InvStG, seit 25.10.2026), der Ratgeber zum
 * Altersvorsorgedepot mit seinen fünf Themen auch (seit 25.10.2026).
 *
 * Gelesen von `proxy.ts` (jede andere Fassung: 308 dorthin), der Server-Seite
 * (hreflang nennt nur diese Fassung), `HreflangTags.js` (dasselbe in der App),
 * dem Routenwächter in `App.jsx`, dem Menü „Für wen“ und „Über uns“ (Eintrag nur
 * in dieser Fassung). Die Sitemap hält `backend/app/services/sitemap.py`
 * (`NUR_IN`) — `test_av_depot_seite_canon.py` hält beide gegeneinander.
 */
export const NUR_IN: Readonly<Record<string, { land: string; sprache: string; code: string }>> = {
  '/altersvorsorgedepot-rechner': { land: 'de', sprache: 'deutsch', code: 'de' },
  '/vorabpauschale-rechner': { land: 'de', sprache: 'deutsch', code: 'de' },
  '/altersvorsorgedepot': { land: 'de', sprache: 'deutsch', code: 'de' },
  '/altersvorsorgedepot/zulagen': { land: 'de', sprache: 'deutsch', code: 'de' },
  '/altersvorsorgedepot/vergleich': { land: 'de', sprache: 'deutsch', code: 'de' },
  '/altersvorsorgedepot/erlaubte-fonds': { land: 'de', sprache: 'deutsch', code: 'de' },
  '/altersvorsorgedepot/auszahlung': { land: 'de', sprache: 'deutsch', code: 'de' },
  '/altersvorsorgedepot/erbfall': { land: 'de', sprache: 'deutsch', code: 'de' },
};

/** Die einzige Fassung einer Seite aus `NUR_IN` — `null`, wenn es sie überall gibt. */
export function einzigeFassung(rest: string): { land: string; sprache: string; code: string } | null {
  return NUR_IN[rest] ?? null;
}

/**
 * Das öffentliche Investorprofil: kein fester Pfad, sondern `PROFIL_PRAEFIX/<name>`
 * — dieselbe Form wie `PROFIL_PFAD` der Sitemap (`backend/app/services/sitemap.py`).
 */
export const PROFIL_PRAEFIX = '/investorprofile';

/**
 * `/pricing` → Seite aus der Liste; `/investorprofile/<name>` → Profil; sonst
 * nichts. Gelesen von der Server-Seite und der Route des Vorschaubilds — eine
 * Adresse, die keine Seite ist, hat auch kein Bild.
 */
export function seiteZu(rest: string): { seite: ServerSeite; profilName: string | null } | null {
  const fest = SERVER_SEITEN[rest];
  if (fest !== undefined) return { seite: fest, profilName: null };
  const teile = rest.split('/');
  if (teile.length === 3 && `/${teile[1]}` === PROFIL_PRAEFIX && teile[2]) {
    return { seite: 'profil', profilName: decodeURIComponent(teile[2]) };
  }
  return null;
}

/**
 * Schlüssel der SEO-Verwaltung (`GET /api/public/seo/{key}`, `useSeo`), oder
 * `null` für eine Seite, die ihre Texte selbst setzt („Über uns“: aus dem
 * Wörterbuch, `ueber_uns.meta.*`).
 */
export const SEO_SCHLUESSEL: Record<ServerSeite, 'landing' | 'landing_backtest' | 'landing_monte_carlo' | 'landing_entnahmeplan' | 'landing_berater' | 'landing_watchlist' | 'landing_depot_check' | 'landing_fire' | 'landing_av_depot' | 'landing_vorabpauschale' | 'pricing' | 'leaderboard_portfolios' | 'leaderboard_architects' | null> = {
  start: 'landing',
  backtest: 'landing_backtest',
  monte_carlo: 'landing_monte_carlo',
  entnahmeplan: 'landing_entnahmeplan',
  berater: 'landing_berater',
  watchlist: 'landing_watchlist',
  depot_check: 'landing_depot_check',
  fire: 'landing_fire',
  av_depot: 'landing_av_depot',
  vorabpauschale: 'landing_vorabpauschale',
  preise: 'pricing',
  ueber_uns: null,
  // Die Entwickler-Doku setzt Titel und Beschreibung aus dem Wörterbuch (`developers.meta.*`).
  entwickler: null,
  community: 'leaderboard_portfolios',
  architekten: 'leaderboard_architects',
  // Das Profil setzt Titel und Beschreibung aus seinen Daten.
  profil: null,
  // Das Lexikon setzt Titel und Beschreibung selbst (ohne Markenzusatz, Entscheidung 25.10.2026).
  lexikon: null,
  lexikon_kennzahl: null,
  // Studie und Musterportfolios setzen Titel und Beschreibung selbst (ohne Markenzusatz).
  studie_entnahme: null,
  musterportfolios: null,
  musterportfolio: null,
  // Ratgeber, Vergleich und Methodik setzen Titel und Beschreibung selbst (ohne Markenzusatz).
  av_ratgeber: null,
  backtest_tools: null,
  pv_alternative: null,
  methodik: null,
  // Der Ratgeber ebenso — Titel und Beschreibung ohne Platzhalter (`/llms.txt`).
  ratgeber_4_prozent: null,
};

/** Seiten, die die Zahlen der Musterportfolios im ersten Rendern brauchen (`/api/public/musterportfolios`). */
export const MIT_MUSTERPORTFOLIOS: readonly ServerSeite[] = ['musterportfolios', 'musterportfolio'];

/** Seiten im Bereich „Community“ — die Verwaltung kann ihn abschalten oder hinter die Anmeldung legen. */
export const IM_COMMUNITY_BEREICH: readonly ServerSeite[] = ['community', 'architekten', 'profil'];

/** Seiten, die den Tarif-Katalog im ersten Rendern brauchen (Preise, Mengen). */
export const MIT_KATALOG: readonly ServerSeite[] = ['preise', 'ueber_uns'];

/**
 * Angemeldet? Erkannt am Cookie `pb_csrf` (`CSRF_COOKIE_NAME` im Backend): Die
 * Sitzungs-Cookies gelten nur unter `/api/` und kommen auf Seitenpfaden nie
 * an; `pb_csrf` setzt die Anmeldung auf `/` und löscht die Abmeldung. Gelesen
 * von den Server-Seiten und der Weiterleitung der Startseite (`proxy.ts`).
 */
export const ANMELDE_SIGNAL = 'pb_csrf';

/** Interner Ordner der Route; die öffentliche Adresse schreibt `next.config.ts` darauf um. */
export const INTERNER_PFAD = '/oeffentlich';
